import React, { FunctionComponent } from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
    ListItemIcon,
    ListItem,
    ListItemText,
    Typography,
    Collapse,
    List,
    Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
    commonStyles,
    theme as muiTheme,
    useSafeIntl,
} from 'bluesquare-components';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../../../utils/usersUtils';
import { listMenuPermission, userHasOneOfPermissions } from '../../users/utils';

const useStyles = makeStyles(theme => ({
    ...commonStyles(theme),
    listItemIcon: {
        minWidth: 35,
    },
    linkButton: {
        color: 'inherit',
        textDecoration: 'none',
        display: 'flex',
    },
}));

type Props = {
    location: Record<string, any>;
    menuItem: Record<string, any>;
    onClick: (path: string, url?: string) => void;
    subMenuLevel?: number;
    currentPath?: string;
    url?: string;
};

const MenuItem: FunctionComponent<Props> = ({
    onClick,
    menuItem,
    location,
    subMenuLevel = 1,
    currentPath = '',
    url = '',
}) => {
    const classes: Record<string, string> = useStyles();
    const currentUser = useCurrentUser();
    const urlLink = url;
    const { formatMessage } = useSafeIntl();
    const path =
        urlLink || !menuItem.key
            ? `${currentPath}`
            : `${currentPath}/${menuItem.key}`;
    const activePath = location.pathname.split('/', subMenuLevel + 1).join('/');
    const isMenuActive = menuItem.isActive
        ? menuItem.isActive(location.pathname)
        : path === activePath;
    const fullPath = `${
        menuItem.extraPath
            ? `${path}/accountId/${currentUser.account?.id}${menuItem.extraPath}`
            : path
    }`;
    const [open, setOpen] = React.useState(isMenuActive);
    const toggleOpen = () => {
        setOpen(!open);
    };
    const color = isMenuActive ? 'primary' : 'inherit';
    const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;
    const subMenuIcon = open ? (
        <ExpandLess color={color} />
    ) : (
        <ExpandMore color={color} />
    );
    const itemStyle = {
        paddingLeft: muiTheme.spacing(subMenuLevel * 2),
    };
    if (hasSubMenu) {
        return (
            <>
                <ListItem
                    style={itemStyle}
                    button
                    onClick={() =>
                        !hasSubMenu ? onClick(path, url) : toggleOpen()
                    }
                >
                    {menuItem.icon && (
                        <ListItemIcon className={classes.listItemIcon}>
                            {menuItem.icon({ color })}
                        </ListItemIcon>
                    )}
                    <ListItemText
                        primary={
                            <Box pl={menuItem.icon ? 0 : 2}>
                                {/* type prop should be variant. Check impact on UI layout before changing */}
                                {/* @ts-ignore */}
                                <Typography type="body2" color={color}>
                                    {menuItem.label.defaultMessage &&
                                        menuItem.label.id &&
                                        formatMessage(menuItem.label)}
                                    {typeof menuItem.label === 'string' &&
                                        menuItem.label}
                                </Typography>
                            </Box>
                        }
                    />
                    {hasSubMenu ? subMenuIcon : null}
                </ListItem>
                {hasSubMenu && (
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {menuItem.subMenu.map(subMenu => {
                                const permissionsList =
                                    listMenuPermission(subMenu);
                                if (
                                    userHasOneOfPermissions(
                                        permissionsList,
                                        currentUser,
                                    )
                                ) {
                                    return (
                                        <MenuItem
                                            key={subMenu.mapKey || subMenu.key}
                                            menuItem={subMenu}
                                            url={subMenu.url}
                                            onClick={subPath =>
                                                onClick(subPath)
                                            }
                                            subMenuLevel={subMenuLevel + 1}
                                            location={location}
                                            currentPath={path}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </List>
                    </Collapse>
                )}
            </>
        );
    }

    return (
        <Link
            className={classes.linkButton}
            to={!hasSubMenu ? fullPath : ''}
            target={urlLink ? '_blank' : ''}
        >
            <ListItem
                style={itemStyle}
                button
                onClick={() =>
                    !hasSubMenu ? onClick(path, url) : toggleOpen()
                }
            >
                {menuItem.icon && (
                    <ListItemIcon className={classes.listItemIcon}>
                        {menuItem.icon({ color })}
                    </ListItemIcon>
                )}
                <ListItemText
                    primary={
                        <Box pl={menuItem.icon ? 0 : 2}>
                            {/* type prop should be variant. Check impact on UI layout before changing */}
                            {/* @ts-ignore */}
                            <Typography type="body2" color={color}>
                                {menuItem.label.defaultMessage &&
                                    menuItem.label.id &&
                                    formatMessage(menuItem.label)}
                                {typeof menuItem.label === 'string' &&
                                    menuItem.label}
                            </Typography>
                        </Box>
                    }
                />
                {hasSubMenu ? subMenuIcon : null}
            </ListItem>
        </Link>
    );
};

export default MenuItem;
